import { useMemo } from 'react';

import { colorConstants } from 'config/colorConstants';

export const genericScenarioTableUiConfig = {
    ids: {
        container: 'generic-scenario-table-container',
    },
    behavior: {
        editOnHover: true,
        editOnClick: true,
        showSaveActions: true,
        isFlattened: true,
    },
    table: {
        heightBuffer: 200,
        minBodyHeight: 220,
        scrollX: 'max-content',
        size: 'small' as const,
        rowSelectionWidth: 44,
        repeatedValueOpacity: 0.3,
    },
    actionButton: {
        ariaLabel: 'Add row',
    },
    footer: {
        recordLabel: 'Showing 50 of 20,000 Records',
        statusLabel: 'Status: In-Progress',
        refreshLabel: 'Last Refresh: 1 min ago',
    },
    aiColumn: {
        key: 'ai',
        dataIndex: 'ai',
        title: 'AI',
        width: 90,
        fixed: 'left' as const,
        showTitleTooltip: true,
        expandAriaLabel: {
            collapsed: 'Expand row',
            expanded: 'Collapse row',
        },
        icon: {
            backgroundColor: colorConstants.breadcrumbSlash,
            lineColor: colorConstants.white,
        },
        parentHintClassName: 'mt-[2px] text-[10px]',
        parentHintColor: colorConstants.tableHintText,
        allDimensionValue: 'All',
        expandableLabels: ['Price', 'Delist'],
    },
    standardColumns: [
        {
            key: 'ppg',
            dataIndex: 'ppg',
            title: 'PPG',
            width: 220,
            fixed: 'left' as const,
            cellType: 'text',
            emptyDisplay: ' ',
            sortable: false,
            filterSortable: true,
            showTitleTooltip: true,
            titleTooltipText: 'Product portfolio group',
        },
        {
            key: 'sku',
            dataIndex: 'sku',
            title: 'SKU',
            width: 260,
            fixed: 'left' as const,
            cellType: 'text',
            emptyDisplay: ' ',
            sortable: true,
            showTitleTooltip: true,
            titleTooltipText: 'Stock keeping unit',
        },
        {
            key: 'channel',
            dataIndex: 'channel',
            title: 'Channel',
            width: 150,
            fixed: 'left' as const,
            cellType: 'text',
            emptyDisplay: ' ',
            sortable: true,
            showTitleTooltip: true,
            titleTooltipText: 'Sales channel',
        },
        {
            key: 'retailer',
            dataIndex: 'retailer',
            title: 'Retailer',
            width: 150,
            fixed: 'left' as const,
            cellType: 'text',
            emptyDisplay: ' ',
            sortable: true,
            showTitleTooltip: true,
            titleTooltipText: 'Retail partner',
        },
        {
            key: 'subBrand',
            dataIndex: 'subBrand',
            title: 'Sub Brand',
            width: 160,
            emptyDisplay: 'Sub Brand',
            sortable: true,
            fixed: 'left' as const,
            showTitleTooltip: true,
            titleTooltipText: 'Sub-brand selector',
            displayClassName: 'text-[12px] leading-[18px]',
            displayColor: colorConstants.blackTitle,
            editor: {
                kind: 'searchableDropdown',
                options: [
                    { label: 'Sub Brand Alpha', value: 'Sub Brand Alpha' },
                    { label: 'Sub Brand Beta', value: 'Sub Brand Beta' },
                    { label: 'Sub Brand Gamma', value: 'Sub Brand Gamma' },
                ],
                placeholder: 'Select sub brand',
                showTooltip: true,
                tooltipText: 'Choose a sub-brand',
            },
        },
        {
            key: 'basePriceElasticity',
            dataIndex: 'basePriceElasticity',
            title: 'Base Price Elasticity',
            width: 120,
            cellType: 'lockedMetric',
            emptyDisplay: '$12.34',
            showTitleTooltip: true,
            titleTooltipText: 'Locked baseline metric',
            showLockIconInTitle: true,
            displayFormat: {
                prefix: '$',
                suffix: '',
                emptyDisplay: '$12.34',
            },
        },
    ],
    kpiGroups: [
        {
            key: 'userInputKpi1',
            title: 'User Input KPI',
            showTitleTooltip: true,
            titleTooltipText: 'User configurable KPI group',
            metrics: [
                {
                    key: 'current',
                    title: 'Current',
                    cellType: 'staticMetric',
                    displayClassName: 'text-[13px] leading-[18px]',
                    displayColor: colorConstants.tableBodyText,
                    displayFormat: { prefix: '$', suffix: '', emptyDisplay: '$12.34' },
                },
                {
                    key: 'chg',
                    title: 'Chg',
                    cellType: 'editableInput',
                    displayClassName: 'text-[12px] leading-[18px]',
                    displayColor: colorConstants.tableMutedText,
                    displayFormat: { prefix: '', suffix: '%', emptyDisplay: '%' },
                    editor: {
                        showTooltip: true,
                        tooltipText: 'Enter percent change',
                        allowedValueType: 'number',
                        allowDecimal: true,
                        decimalPlaces: 2,
                        prefixText: '',
                        suffixText: '%',
                    },
                },
                {
                    key: 'new',
                    title: 'New',
                    cellType: 'editableInput',
                    displayClassName: 'text-[12px] leading-[18px]',
                    displayColor: colorConstants.tableMutedText,
                    displayFormat: { prefix: '$', suffix: '', emptyDisplay: '$' },
                    editor: {
                        showTooltip: true,
                        tooltipText: 'Enter new value',
                        allowedValueType: 'number',
                        allowDecimal: true,
                        decimalPlaces: 2,
                        prefixText: '$',
                        suffixText: '',
                    },
                },
            ],
        },
        {
            key: 'userInputKpi2',
            title: 'User Input KPI',
            showTitleTooltip: true,
            titleTooltipText: 'User configurable KPI group',
            metrics: [
                {
                    key: 'current',
                    title: 'Current',
                    cellType: 'staticMetric',
                    displayClassName: 'text-[13px] leading-[18px]',
                    displayColor: colorConstants.tableBodyText,
                    displayFormat: { prefix: '$', suffix: '', emptyDisplay: '$12.34' },
                },
                {
                    key: 'chg',
                    title: 'Chg',
                    cellType: 'editableInput',
                    displayClassName: 'text-[12px] leading-[18px]',
                    displayColor: colorConstants.tableMutedText,
                    displayFormat: { prefix: '', suffix: '%', emptyDisplay: '%' },
                    editor: {
                        showTooltip: true,
                        tooltipText: 'Enter percent change',
                        allowedValueType: 'number',
                        allowDecimal: true,
                        decimalPlaces: 2,
                        prefixText: '',
                        suffixText: '%',
                    },
                },
                {
                    key: 'new',
                    title: 'New',
                    cellType: 'editableInput',
                    displayClassName: 'text-[12px] leading-[18px]',
                    displayColor: colorConstants.tableMutedText,
                    displayFormat: { prefix: '$', suffix: '', emptyDisplay: '$' },
                    editor: {
                        showTooltip: true,
                        tooltipText: 'Enter new value',
                        allowedValueType: 'number',
                        allowDecimal: true,
                        decimalPlaces: 2,
                        prefixText: '$',
                        suffixText: '',
                    },
                },
            ],
        },
        {
            key: 'userInputKpi3',
            title: 'User Input KPI',
            showTitleTooltip: true,
            titleTooltipText: 'User configurable KPI group',
            metrics: [
                {
                    key: 'current',
                    title: 'Current',
                    cellType: 'staticMetric',
                    displayClassName: 'text-[13px] leading-[18px]',
                    displayColor: colorConstants.tableBodyText,
                    displayFormat: { prefix: '$', suffix: '', emptyDisplay: '$12.34' },
                },
                {
                    key: 'chg',
                    title: 'Chg',
                    cellType: 'editableInput',
                    displayClassName: 'text-[12px] leading-[18px]',
                    displayColor: colorConstants.tableMutedText,
                    displayFormat: { prefix: '', suffix: '%', emptyDisplay: '%' },
                    editor: {
                        showTooltip: true,
                        tooltipText: 'Enter percent change',
                        allowedValueType: 'number',
                        allowDecimal: true,
                        decimalPlaces: 2,
                        prefixText: '',
                        suffixText: '%',
                    },
                },
                {
                    key: 'new',
                    title: 'New',
                    cellType: 'editableInput',
                    displayClassName: 'text-[12px] leading-[18px]',
                    displayColor: colorConstants.tableMutedText,
                    displayFormat: { prefix: '$', suffix: '', emptyDisplay: '$' },
                    editor: {
                        showTooltip: true,
                        tooltipText: 'Enter new value',
                        allowedValueType: 'number',
                        allowDecimal: true,
                        decimalPlaces: 2,
                        prefixText: '$',
                        suffixText: '',
                    },
                },
            ],
        },
    ],
    defaultKpiColumns: [
        {
            key: 'implementFrom',
            dataIndex: 'implementFrom',
            title: 'Implement From:',
            width: 160,
            cellType: 'datePicker',
            emptyDisplay: 'DD/MM/YY - DD/MM/YY',
            editor: {
                placeholder: 'DD/MM/YY - DD/MM/YY',
                startWeekday: 'friday' as const,
                showTooltip: true,
                tooltipText: '',
            },
        },
        {
            key: 'competitorFollows',
            dataIndex: 'competitorFollows',
            title: 'Competitor Follows',
            width: 120,
            cellType: 'checkbox',
            editor: {
                showCheckedTooltip: true,
                checkedTooltipItems: [
                    { label: 'Vendor name one', value: '5 PPGs' },
                    { label: 'Vendor name two', value: '15 PPGs' },
                    { label: 'Vendor name three', value: '3 PPGs' },
                    { label: 'Vendor name four', value: '2 PPGs' },
                ],
            },
        },
    ],
};

export type GenericScenarioTableUiConfig = typeof genericScenarioTableUiConfig;

const TREE_CHILD_COUNT = 3;
const ALL_VALUE = genericScenarioTableUiConfig.aiColumn.allDimensionValue;
const EMPTY_KPI = {
    userInputKpi1: { current: '12.34', chg: '', new: '' },
    userInputKpi2: { current: '12.34', chg: '', new: '' },
    userInputKpi3: { current: '12.34', chg: '', new: '' },
};

const rangeFromOne = (count: number) => Array.from({ length: count }, (_, index) => index + 1);

const createDummyRow = ({
    key,
    ai = '',
    ppg,
    sku,
    channel,
    retailer,
    subBrand = 'Sub Brand Alpha',
    implementFrom = '',
    competitorFollows = false,
    segment = 'Segment A',
    parentHint,
    isParent = false,
    children,
    kpi = EMPTY_KPI,
}: any) => ({
    key,
    ai,
    ppg,
    sku,
    channel,
    retailer,
    subBrand,
    implementFrom,
    competitorFollows,
    segment,
    basePriceElasticity: '12.34',
    parentHint,
    isParent,
    kpi,
    ...(children ? { children } : {}),
});

const createRetailerRows = (parentKey: string, ppg: string, sku: string, channel: string) =>
    rangeFromOne(TREE_CHILD_COUNT).map((retailerIndex) =>
        createDummyRow({
            key: `${parentKey}-retailer-${retailerIndex}`,
            ppg,
            sku,
            channel,
            retailer: `Retailer ${retailerIndex}`,
            competitorFollows: retailerIndex === 1,
        }),
    );

const createChannelRows = (parentKey: string, ppg: string, sku: string) =>
    rangeFromOne(TREE_CHILD_COUNT).map((channelIndex) => {
        const channel = `Channel ${channelIndex}`;
        const channelKey = `${parentKey}-channel-${channelIndex}`;

        return createDummyRow({
            key: channelKey,
            ppg,
            sku,
            channel,
            retailer: ALL_VALUE,
            isParent: true,
            children: createRetailerRows(channelKey, ppg, sku, channel),
        });
    });

const createPpgRows = (brandKey: string, brand: string) =>
    rangeFromOne(TREE_CHILD_COUNT).map((ppgIndex) => {
        const sku = `PPG ${ppgIndex}`;
        const ppgKey = `${brandKey}-ppg-${ppgIndex}`;

        return createDummyRow({
            key: ppgKey,
            ppg: brand,
            sku,
            channel: ALL_VALUE,
            retailer: ALL_VALUE,
            isParent: true,
            children: createChannelRows(ppgKey, brand, sku),
        });
    });

const LONG_PPG_NAME = 'Total Packaged Nuts & Seeds_national_ very very long name that wraps beyond two lines';

const buildDummyRows = () =>
    rangeFromOne(TREE_CHILD_COUNT).map((brandIndex) => {
        const brand = brandIndex === 1 ? LONG_PPG_NAME : `Brand ${brandIndex}`;
        const brandKey = `row-brand-${brandIndex}`;

        return createDummyRow({
            key: brandKey,
            ai: 'Price',
            ppg: brand,
            sku: ALL_VALUE,
            channel: ALL_VALUE,
            retailer: ALL_VALUE,
            isParent: true,
            children: createPpgRows(brandKey, brand),
        });
    });

const getKpiDataIndex = (groupKey: string, metricKey: string) => `${groupKey}_${metricKey}`;

const withConfiguredRowValues = (row: any) => {
    const nextRow: any = { ...row };

    genericScenarioTableUiConfig.standardColumns.forEach((columnConfig) => {
        const dataIndex = columnConfig.dataIndex;
        if (nextRow[dataIndex] === undefined || nextRow[dataIndex] === null) {
            nextRow[dataIndex] = '';
        }
    });

    genericScenarioTableUiConfig.kpiGroups.forEach((groupConfig) => {
        groupConfig.metrics.forEach((metricConfig) => {
            const dataIndex = getKpiDataIndex(groupConfig.key, metricConfig.key);
            nextRow[dataIndex] = row.kpi?.[groupConfig.key]?.[metricConfig.key] ?? '';
        });
    });

    genericScenarioTableUiConfig.defaultKpiColumns.forEach((columnConfig) => {
        const dataIndex = columnConfig.dataIndex;
        if (nextRow[dataIndex] === undefined || nextRow[dataIndex] === null) {
            nextRow[dataIndex] = '';
        }
    });

    if (Array.isArray(row.children)) {
        nextRow.children = row.children.map((child: any) => withConfiguredRowValues(child));
    }

    return nextRow;
};

export const useGenericAntdScenarioTable = () => {
    const tableUiConfig = genericScenarioTableUiConfig;

    const sourceRows = useMemo(() => buildDummyRows().map((row) => withConfiguredRowValues(row)), []);

    // Replace dummy rows above with the scenario table API response.
    return {
        tableUiConfig,
        sourceRows,
        isLoading: false,
    };
};
